import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Utensils, Truck, Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: "",
  category: "",
  description: "",
  price: "",
  protein: "",
  image: "",
  popular: false,
  available: true,
  weekly_limit: "",
  menu_type: "meal_prep",
};

export const MenuItemManager = ({ menuItems, onChanged }) => {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      category: item.category || "",
      description: item.description || "",
      price: String(item.price ?? ""),
      protein: item.protein || "",
      image: item.image || "",
      popular: !!item.popular,
      available: item.available !== false,
      weekly_limit: item.weekly_limit ?? "",
      menu_type: item.menu_type || "meal_prep",
    });
    setOpen(true);
  };

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async () => {
    if (!form.name.trim() || !form.category.trim() || !form.description.trim()) {
      toast.error("Name, category, and description are required");
      return;
    }
    const priceNum = parseFloat(form.price);
    if (Number.isNaN(priceNum) || priceNum < 0) {
      toast.error("Enter a valid price");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim(),
        description: form.description.trim(),
        price: priceNum,
        protein: form.protein || "",
        image: form.image || "",
        popular: !!form.popular,
        available: !!form.available,
        weekly_limit:
          form.weekly_limit === "" || form.weekly_limit === null
            ? null
            : parseInt(form.weekly_limit, 10),
        menu_type: form.menu_type || "meal_prep",
      };
      if (editingId) {
        await axios.put(
          `${BACKEND_URL}/api/admin/menu-items/${editingId}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Menu item updated");
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/menu-items`, payload, {
          withCredentials: true,
        });
        toast.success("Menu item added");
      }
      setOpen(false);
      onChanged && onChanged();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to save menu item";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/menu-items/${item.id}`,
        { available: !item.available },
        { withCredentials: true }
      );
      toast.success(item.available ? "Item hidden from menu" : "Item is now live");
      onChanged && onChanged();
    } catch {
      toast.error("Failed to update availability");
    }
  };

  const remove = async (item) => {
    if (
      !window.confirm(
        `Delete "${item.name}"? This permanently removes it from the menu.`
      )
    )
      return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/menu-items/${item.id}`, {
        withCredentials: true,
      });
      toast.success("Menu item deleted");
      onChanged && onChanged();
    } catch {
      toast.error("Failed to delete item");
    }
  };

  const filtered = menuItems.filter(
    (it) => filter === "all" || (it.menu_type || "meal_prep") === filter
  );

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Label className="text-sm text-gray-600">Filter:</Label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="menu-type-filter"
            className="border rounded px-3 py-1.5 text-sm"
          >
            <option value="all">All</option>
            <option value="meal_prep">Meal Prep</option>
            <option value="food_truck">Food Truck</option>
          </select>
        </div>
        <Button
          onClick={openCreate}
          data-testid="menu-add-item-btn"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" /> Add Menu Item
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((item) => (
          <Card
            key={item.id}
            className={`border-2 ${item.available === false ? "opacity-60" : ""}`}
            data-testid={`menu-item-card-${item.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {item.image ? (
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Utensils className="h-6 w-6 text-stone-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{item.name}</p>
                  <p className="text-emerald-600 font-semibold">
                    ${item.price.toFixed(2)}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <Badge variant="outline" className="text-xs">
                      {item.category}
                    </Badge>
                    {item.menu_type === "food_truck" && (
                      <Badge className="bg-orange-100 text-orange-800 text-xs border-orange-200">
                        <Truck className="h-3 w-3 mr-1" /> Food Truck
                      </Badge>
                    )}
                    {item.popular && (
                      <Badge className="bg-amber-500 text-white text-xs">
                        Popular
                      </Badge>
                    )}
                    {item.available === false && (
                      <Badge className="bg-gray-200 text-gray-700 text-xs">
                        Hidden
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(item)}
                  data-testid={`menu-edit-btn-${item.id}`}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleAvailability(item)}
                  data-testid={`menu-toggle-btn-${item.id}`}
                >
                  {item.available === false ? (
                    <>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Show
                    </>
                  ) : (
                    <>
                      <EyeOff className="h-3.5 w-3.5 mr-1" /> Hide
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(item)}
                  data-testid={`menu-delete-btn-${item.id}`}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Edit Menu Item" : "Add Menu Item"}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? "Update the details for this menu item."
                : "Add a new item to your Meal Prep or Food Truck menu."}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-4 py-2">
            <div>
              <Label>Menu</Label>
              <select
                value={form.menu_type}
                onChange={(e) => setField("menu_type", e.target.value)}
                data-testid="menu-form-menu-type"
                className="w-full border rounded px-3 py-2 mt-1 text-sm"
              >
                <option value="meal_prep">Meal Prep</option>
                <option value="food_truck">Food Truck</option>
              </select>
            </div>

            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. Jerk Chicken Bowl"
                data-testid="menu-form-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                  placeholder="e.g. Bowls, Sandwiches"
                  data-testid="menu-form-category"
                />
              </div>
              <div>
                <Label>Price (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price}
                  onChange={(e) => setField("price", e.target.value)}
                  placeholder="12.00"
                  data-testid="menu-form-price"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                rows={3}
                placeholder="Short description shown on the menu"
                data-testid="menu-form-description"
                className="w-full border rounded px-3 py-2 mt-1 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Protein (optional)</Label>
                <Input
                  value={form.protein}
                  onChange={(e) => setField("protein", e.target.value)}
                  placeholder="Chicken, Steak, ..."
                  data-testid="menu-form-protein"
                />
              </div>
              <div>
                <Label>Weekly Limit (optional)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.weekly_limit}
                  onChange={(e) => setField("weekly_limit", e.target.value)}
                  placeholder="Leave blank for unlimited"
                  data-testid="menu-form-weekly-limit"
                />
              </div>
            </div>

            <div>
              <Label>Image URL (optional)</Label>
              <Input
                value={form.image}
                onChange={(e) => setField("image", e.target.value)}
                placeholder="https://..."
                data-testid="menu-form-image"
              />
              {form.image && (
                <img
                  src={form.image}
                  alt="preview"
                  className="mt-2 w-24 h-24 object-cover rounded border"
                />
              )}
            </div>

            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.available}
                  onChange={(e) => setField("available", e.target.checked)}
                  data-testid="menu-form-available"
                />
                Available (visible on menu)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.popular}
                  onChange={(e) => setField("popular", e.target.checked)}
                  data-testid="menu-form-popular"
                />
                Popular
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid="menu-form-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              data-testid="menu-form-save"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {saving ? "Saving..." : editingId ? "Save Changes" : "Add Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MenuItemManager;
